// Build-time platform discriminator. `__VALKENHALL_WEB__`,
// `__VALKENHALL_VERSION__` and `__VALKENHALL_WEB_CHANNEL__` are injected
// by `scripts/build-web.js --web` via Bun.build defines. In desktop builds
// and tests none is defined, so every helper returns its desktop default
// and the desktop bundle is byte-for-byte unaffected by web-only branches.
// (All three identifiers are declared as globals in eslint.config.js.)

export function isWeb() {
  return typeof __VALKENHALL_WEB__ !== 'undefined' && !!__VALKENHALL_WEB__;
}

// The app version baked in at build time. Desktop gets its version from
// the updater status endpoint instead (see versionCheck.js); the web build
// has no local server, so the build is the only source of truth.
export function webClientVersion() {
  return typeof __VALKENHALL_VERSION__ !== 'undefined' ? (__VALKENHALL_VERSION__ || null) : null;
}

// The server channel baked in at build time: 'stable' on play.valkenhall.com,
// 'beta' on qa.valkenhall.com (the RC/QA deploy). Null on desktop, where the
// channel is a user preference instead (see updateChannel.js).
export function webBakedChannel() {
  return typeof __VALKENHALL_WEB_CHANNEL__ !== 'undefined' ? (__VALKENHALL_WEB_CHANNEL__ || null) : null;
}

// Phone-class device: web build, touch-first, and a screen whose short
// side is under 500 CSS px. Phones get the mobile layout (full-viewport
// canvas, boosted tokens, landscape-only); iPads (short side >= 744) and
// desktops keep the 16:9 letterboxed canvas. Math.min makes the check
// rotation-invariant on browsers that swap screen.width/height.
// Minimum supported screen for the browser build on touch devices: the
// 13-inch iPad Pro class (logical short edge 1024+, matching the 12.9"
// at 1024 and the 13" at 1032). Anything smaller renders the
// unsupported-screen gate instead of the app. Pointer-precision devices
// (laptops, desktops) are never gated, whatever their screen or window
// size. The `?phone=1` tester escape from isPhoneLayout bypasses the
// gate so the phone layout stays reachable during development.
export function isUnsupportedScreen() {
  if (!isWeb() || typeof window === 'undefined') return false;
  // QA builds (beta channel, qa.valkenhall.com) never gate: the mobile
  // layout is developed against real phones there while it stays behind
  // this gate on the prod build until it's ready.
  if (webBakedChannel() === 'beta') return false;
  try {
    if (new URLSearchParams(window.location.search).get('phone') === '1') return false;
  } catch (_) { /* malformed search string: fall through to real detection */ }
  if (!window.matchMedia?.('(pointer: coarse)')?.matches) return false;
  const s = window.screen;
  return Math.min(s?.width || Infinity, s?.height || Infinity) < 1024;
}

export function isPhoneLayout() {
  if (!isWeb() || typeof window === 'undefined') return false;
  // ponytail: `?phone=1` forces the phone branch on a desktop browser.
  // `pointer: coarse` cannot be emulated by viewport resizing (only by a
  // real device or DevTools device mode), so without this escape hatch the
  // mobile screens can only be exercised on physical hardware. Same
  // URL-param idiom as isDevBuild() in devMode.js.
  try {
    if (new URLSearchParams(window.location.search).get('phone') === '1') return true;
  } catch (_) { /* malformed search string: fall through to real detection */ }
  if (!window.matchMedia?.('(pointer: coarse)')?.matches) return false;
  const s = window.screen;
  return Math.min(s?.width || Infinity, s?.height || Infinity) < 500;
}
